import type { FC, ReactNode } from 'react'

import Link from 'next/link'

import { HAIRLINE, HAIRLINE_SOFT, INK_BODY, INK_FAINT, INK_MUTED } from '@/constants/riffle-ui'
import { contactEmail, navLinks, pressEmail } from '@/constants/site'

const details: { label: string; value: string }[] = [
    { label: 'Objects', value: '22' },
    { label: 'Formations', value: 'Six' },
    { label: 'Edition', value: '2026' }
]

const eyebrow = {
    fontSize: 10,
    letterSpacing: '0.26em',
    color: INK_FAINT
} as const

export const Footer: FC = (): ReactNode => {
    return (
        <footer
            id='site-footer'
            className='relative z-10 px-6 pb-9 pt-24 sm:px-12 sm:pb-12 sm:pt-32'
        >
            <div className='mx-auto w-full max-w-6xl'>
                <div style={{ height: 1, background: HAIRLINE }} />

                <div className='grid grid-cols-2 gap-x-8 gap-y-12 pt-10 sm:pt-12 lg:grid-cols-4 lg:gap-x-12'>
                    {/* brand */}
                    <div className='col-span-2 lg:col-span-1'>
                        <div
                            className='font-mono uppercase'
                            style={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.32em', opacity: 0.8 }}
                        >
                            Riffle
                        </div>

                        <div
                            className='font-mono uppercase'
                            style={{ fontSize: 9, letterSpacing: '0.3em', opacity: 0.4, marginTop: 4 }}
                        >
                            ♪ The 2026 Library
                        </div>

                        <p
                            className='mt-5 text-[13px] leading-[1.7] sm:mt-6'
                            style={{ maxWidth: 300, color: INK_MUTED }}
                        >
                            Twenty-two objects, photographed on one sweep under one light, and
                            dealt as a single deck.
                        </p>
                    </div>

                    {/* pages */}
                    <div>
                        <div className='font-mono uppercase' style={eyebrow}>
                            Pages
                        </div>

                        <ul className='mt-4 space-y-2.5'>
                            {navLinks.map((link) => (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        className='riffle-link text-[14px] tracking-tight'
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* direct */}
                    <div>
                        <div className='font-mono uppercase' style={eyebrow}>
                            Direct
                        </div>

                        <ul className='mt-4 space-y-2.5'>
                            <li>
                                <a
                                    href={`mailto:${contactEmail}`}
                                    className='riffle-link text-[14px] tracking-tight'
                                >
                                    {contactEmail}
                                </a>
                            </li>

                            <li>
                                <a
                                    href={`mailto:${pressEmail}`}
                                    className='riffle-link text-[14px] tracking-tight'
                                >
                                    {pressEmail}
                                </a>
                            </li>

                            <li className='text-[14px] tracking-tight' style={{ color: INK_BODY, opacity: 0.62 }}>
                                Hackney Wick, London
                            </li>
                        </ul>
                    </div>

                    {/* the set, in the section's spec-block form */}
                    <div>
                        <div className='font-mono uppercase' style={eyebrow}>
                            The set
                        </div>

                        <div className='mt-4 space-y-2.5'>
                            {details.map((item) => (
                                <div key={item.label} className='flex items-baseline justify-between gap-4'>
                                    <span
                                        className='font-mono uppercase'
                                        style={{ fontSize: 10, letterSpacing: '0.26em', opacity: 0.45 }}
                                    >
                                        {item.label}
                                    </span>

                                    <span className='text-[14px] tracking-tight' style={{ opacity: 0.95 }}>
                                        {item.value}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* oversized sign-off, sitting as texture rather than type */}
                <div
                    aria-hidden
                    className='font-playfair mt-16 select-none overflow-hidden sm:mt-20'
                    style={{
                        fontSize: 'clamp(3.5rem, 14vw, 10rem)',
                        lineHeight: 0.85,
                        letterSpacing: '-0.03em',
                        color: 'rgba(255,255,255,0.055)'
                    }}
                >
                    Riffle
                </div>

                <div className='mt-10 sm:mt-12' style={{ height: 1, background: HAIRLINE_SOFT }} />

                <div className='flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:justify-between'>
                    <span
                        className='font-mono uppercase'
                        style={{ fontSize: 9, letterSpacing: '0.3em', color: INK_FAINT }}
                    >
                        © 2026 Riffle
                    </span>

                    <span
                        className='font-mono uppercase'
                        style={{ fontSize: 9, letterSpacing: '0.3em', color: INK_FAINT }}
                    >
                        Shot in studio · Graded as one set
                    </span>
                </div>
            </div>
        </footer>
    )
}
